import { type CSSProperties } from 'react'
import {
  compactTime,
  dueTone,
  monthName,
  toDate,
  weekdayOf,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
} from '@app/shared'

import { monthEntries, monthMatrix, MONTH_ROWS, type MonthEntry } from './derive'
import { PALETTE, blockMotion, type PaletteRow } from './palette'
import { anchorOfElement, type PeekAnchor } from '../../lib/popover'

// ============================================================================
// THE MONTH GRID.
//
// docs/design/calendar-brief.md → Scope says "Ship Day + Week. Month is a stub … UNLESS TOLD
// OTHERWISE". The owner told otherwise (2026-08-28), so this is the view that replaces the
// "Month view coming" panel. There is NO mockup for it in docs/design/handoff — the handoff
// draws 11a (week) and 11b (day) only — so every value here is borrowed from those two screens
// rather than invented: the panel shell is the week card's, the weekday strip is the week
// header's type, the cells wear the same `cal-today-wash` / `cal-weekend` grounds, and an entry
// is a smaller version of the week's own all-day pill. Nothing new is designed here.
//
// WHAT A MONTH IS, TO THIS APP: a wider range of the same read. `useCalendarRange` takes a span
// and the Worker assembles up to 62 days in one request, so the month grid's 42 cells are one
// query with no new endpoint, no new cache key shape and no second opinion about what a day
// holds. That is also why the four honesty rules carry over untouched:
//
//   1. CANVAS AND GOOGLE ARE READ-ONLY. A block LifeOS does not own renders as a `<div>` here
//      exactly as it does on the grid — the click opens the detail peek, never an editor, and
//      there is no drag affordance because MONTH HAS NO DRAG AT ALL in v1. Dropping a block on
//      a day would have to invent a time of day for it, and nothing on this screen invents.
//   2. NOTHING INVENTS A SLOT — and nothing invents a proposal either: `monthEntries` counts
//      through `countedBlocks`, so an unaccepted suggestion cannot appear in a cell.
//   3. THE CELL STATES WHAT IT HID. When a day holds more than fits, the last line says how
//      many are not drawn and opens that day. A cell that silently truncates is a cell claiming
//      a lighter day than the one you have.
//   5. A DEADLINE CANVAS GAVE NO TIME FOR SAYS SO. Its time column reads "Due", never 11:59p,
//      and its hover text carries the full `label` the due row prints.
// ============================================================================

/**
 * How many entries a cell draws before it starts counting instead.
 *
 * A FIXED NUMBER, NOT A MEASUREMENT. The alternative is measuring the cell and fitting as many
 * rows as happen to go in, which makes the "+N more" line appear and vanish as the window is
 * resized — the screen changing what it claims about a day for a reason that has nothing to do
 * with the day. Three plus the counted line is what fits the six-row grid at every size this app
 * ships at, and the count is honest at any height.
 */
const MAX_PER_CELL = 3

const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

export function MonthView({
  anchor,
  today,
  blocks,
  dueItems,
  allDayEvents = [],
  onOpenDay,
  onOpenBlock,
  onOpenDue,
  onOpenAllDay,
}: {
  /** Any day in the month to draw. The grid covers the whole month it falls in. */
  anchor: ISODate
  today: ISODate | undefined
  blocks: CalendarBlock[]
  dueItems: CalendarDueItem[]
  allDayEvents?: CalendarAllDayEvent[]
  /** Clicking a day — its number, its empty ground, or its "+N more" — opens the day view. */
  onOpenDay: (date: ISODate) => void
  /**
   * THE SAME THREE HANDLERS THE WEEK AND DAY VIEWS PASS, deliberately not month-specific ones.
   * An editable block opens the composer, a Canvas class or Google event opens the peek, and
   * both are placed by the shared `popover.ts` — one panel per kind of thing, wherever you
   * clicked it from.
   */
  onOpenBlock: (block: CalendarBlock, anchor: PeekAnchor) => void
  onOpenDue: (item: CalendarDueItem, anchor: PeekAnchor) => void
  onOpenAllDay: (event: CalendarAllDayEvent, anchor: PeekAnchor) => void
}) {
  const month = anchor.slice(0, 7)
  const cells = monthMatrix(anchor)

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-panel border border-line-2 bg-surface shadow-calendar">
      <div className="grid flex-none grid-cols-7 border-b border-line-4">
        {WEEKDAYS.map((name) => (
          <div
            key={name}
            className="box-border border-r border-cal-column py-[9px] text-center text-[9.5px] font-semibold leading-none tracking-[.08em] text-microcopy last:border-r-0"
          >
            {name}
          </div>
        ))}
      </div>

      {/* SIX EQUAL ROWS, and `minmax(0,…)` on both axes for the same reason the week's column
          track list has it: a cell's automatic minimum is its CONTENT's, so one long Canvas
          title would otherwise push a row taller than its share and the grid would stop being
          a grid. `overflow-hidden` on the cell is what makes the "+N more" count the truth
          rather than a decoration over spilling rows. */}
      <div
        className="grid min-h-0 flex-1 grid-cols-7"
        style={{ gridTemplateRows: `repeat(${MONTH_ROWS}, minmax(0, 1fr))` }}
      >
        {cells.map((date) => (
          <MonthCell
            key={date}
            date={date}
            outside={date.slice(0, 7) !== month}
            today={today}
            entries={monthEntries(date, blocks, dueItems, allDayEvents)}
            onOpenDay={onOpenDay}
            onOpenBlock={onOpenBlock}
            onOpenDue={onOpenDue}
            onOpenAllDay={onOpenAllDay}
          />
        ))}
      </div>
    </div>
  )
}

function MonthCell({
  date,
  outside,
  today,
  entries,
  onOpenDay,
  onOpenBlock,
  onOpenDue,
  onOpenAllDay,
}: {
  date: ISODate
  /** A leading or trailing day from a neighbouring month. Drawn, but quieter. */
  outside: boolean
  today: ISODate | undefined
  entries: MonthEntry[]
  onOpenDay: (date: ISODate) => void
  onOpenBlock: (block: CalendarBlock, anchor: PeekAnchor) => void
  onOpenDue: (item: CalendarDueItem, anchor: PeekAnchor) => void
  onOpenAllDay: (event: CalendarAllDayEvent, anchor: PeekAnchor) => void
}) {
  const isToday = date === today
  const shown = entries.slice(0, MAX_PER_CELL)
  const hidden = entries.length - shown.length

  return (
    <div
      // The same guard `GridColumn` uses: only a click on the cell ITSELF opens the day. A click
      // that landed on an entry was a click on that entry, and both of them firing is the screen
      // guessing which one was meant.
      onClick={(e) => {
        if (e.target !== e.currentTarget) return
        onOpenDay(date)
      }}
      className={[
        'box-border flex min-h-0 min-w-0 flex-col gap-[2px] overflow-hidden border-b border-r border-cal-column px-[5px] pb-[4px] pt-[3px]',
        isToday ? 'bg-cal-today-wash' : outside ? 'bg-cal-weekend' : '',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={() => onOpenDay(date)}
        aria-label={`Open ${monthName(date)} ${toDate(date).getUTCDate()}`}
        // A LIGHT INK ON A DARK GROUND for today's filled pip — the same call the composer's
        // Save button makes, and for the same reason: a 10% wash of the neutral default is
        // invisible on `bg-ink`, so the wash has to come from the surface hue instead.
        style={isToday ? ({ '--mo-rule': 'var(--color-surface)' } as CSSProperties) : undefined}
        className={[
          'mo-ink mo-press flex-none self-start rounded-full text-center text-[10.5px] leading-[17px]',
          isToday
            ? 'size-[17px] bg-ink font-semibold text-surface'
            : outside
              ? 'px-[5px] font-normal text-faintest-2'
              : weekdayOf(date) === 0 || weekdayOf(date) === 6
                ? 'px-[5px] font-normal text-muted'
                : 'px-[5px] font-medium text-ink-2',
        ].join(' ')}
      >
        {toDate(date).getUTCDate()}
      </button>

      {shown.map((entry) => (
        <MonthEntryRow
          key={entry.id}
          entry={entry}
          today={today}
          onOpenBlock={onOpenBlock}
          onOpenDue={onOpenDue}
          onOpenAllDay={onOpenAllDay}
        />
      ))}

      {/* RULE 3 IN ONE LINE. The cell says how many it could not draw and opens the day that
          holds them — "hidden is not missing" is the whole design system's first invariant, and
          a month cell is the easiest place in the app to break it by accident. */}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => onOpenDay(date)}
          className="mo-ink mo-press flex-none self-start rounded-keycap px-[4px] py-[1px] text-[9px] font-semibold leading-none text-microcopy"
          title={`${hidden} more on this day. Open the day to see ${hidden === 1 ? 'it' : 'them'}.`}
        >
          +{hidden} more
        </button>
      )}
    </div>
  )
}

/**
 * One line in a cell: the time, then the title, truncated.
 *
 * THE TIME COLUMN NEVER FILLS ITSELF IN. A block prints its start; a whole-day Google event
 * prints Google's own "All day"; a deadline prints the minute Canvas gave, or the word "Due"
 * when Canvas gave none (brief rule 5). There is no fourth branch where the screen picks an
 * hour, and the full honest phrase rides in the hover text at every width.
 */
function MonthEntryRow({
  entry,
  today,
  onOpenBlock,
  onOpenDue,
  onOpenAllDay,
}: {
  entry: MonthEntry
  today: ISODate | undefined
  onOpenBlock: (block: CalendarBlock, anchor: PeekAnchor) => void
  onOpenDue: (item: CalendarDueItem, anchor: PeekAnchor) => void
  onOpenAllDay: (event: CalendarAllDayEvent, anchor: PeekAnchor) => void
}) {
  if (entry.kind === 'block') {
    const block = entry.block
    const palette = PALETTE[block.source]
    return (
      <EntryShell
        palette={palette}
        // RULE 1, THE SAME BRANCH `BlockChip` TAKES. A Canvas class or a mirrored Google event is
        // a `<div>`: it opens the peek, and there is nothing on it that looks like an editor.
        control={block.editable}
        time={compactTime(block.startMinutes)}
        label={block.title}
        hint={[
          block.title,
          block.source === 'canvas'
            ? 'from Canvas · read-only'
            : block.source === 'google'
              ? 'from Google · read-only'
              : null,
          block.location,
        ]
          .filter(Boolean)
          .join(' · ')}
        onOpen={(at) => onOpenBlock(block, at)}
      />
    )
  }

  if (entry.kind === 'due') {
    const item = entry.item
    return (
      <EntryShell
        palette={PALETTE[today ? dueTone(item, today) : 'canvas']}
        control={false}
        time={item.hasTime && item.dueMinutes !== null ? compactTime(item.dueMinutes) : 'Due'}
        label={item.title}
        // `label` is the due row's own literal copy — "PS4 · 11:59p" or "… · no time given".
        hint={`${item.label}${item.courseCode ? ` · ${item.courseCode}` : ''} · from Canvas`}
        onOpen={(at) => onOpenDue(item, at)}
      />
    )
  }

  const event = entry.event
  return (
    <EntryShell
      palette={PALETTE.google}
      control={false}
      time="All day"
      label={event.title}
      italic={event.status === 'tentative'}
      hint={[
        'All day, from Google Calendar',
        event.location ?? undefined,
        event.status === 'tentative' ? 'marked tentative in Google' : undefined,
      ]
        .filter(Boolean)
        .join(' · ')}
      onOpen={(at) => onOpenAllDay(event, at)}
    />
  )
}

function EntryShell({
  palette,
  control,
  time,
  label,
  hint,
  italic,
  onOpen,
}: {
  palette: PaletteRow
  /** True only for a block LifeOS owns. Everything else is read-only, so it is not a `<button>`. */
  control: boolean
  time: string
  label: string
  hint: string
  italic?: boolean
  onOpen: (anchor: PeekAnchor) => void
}) {
  // 12d, and the mechanic is picked by RADIUS, not by what this is: `rounded-keycap` is 6px, so
  // a month entry takes `.mo-rule` — the straight left rule in the lane's own hue plus a tint
  // that deepens the one it already wears. Exactly what a week-view block does, one size down.
  const className = [
    'mo-rule mo-press flex flex-none items-center gap-[4px] overflow-hidden rounded-keycap border-l-[2px] px-[4px] py-[1px] text-left',
    palette.tint,
    palette.rule,
  ].join(' ')

  const body = (
    <>
      <span className={['flex-none text-[8.5px] font-semibold leading-none', palette.ink2].join(' ')}>
        {time}
      </span>
      <span
        className={[
          'min-w-0 flex-1 truncate text-[9.5px] font-semibold leading-[1.35]',
          palette.ink,
          italic ? 'italic' : '',
        ].join(' ')}
      >
        {label}
      </span>
    </>
  )

  if (control) {
    return (
      <button
        type="button"
        style={blockMotion(palette)}
        className={className}
        title={hint}
        onClick={(e) => {
          e.stopPropagation()
          onOpen(anchorOfElement(e.currentTarget))
        }}
      >
        {body}
      </button>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      style={blockMotion(palette)}
      className={`${className} cursor-pointer`}
      title={hint}
      onClick={(e) => {
        e.stopPropagation()
        onOpen(anchorOfElement(e.currentTarget))
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        onOpen(anchorOfElement(e.currentTarget))
      }}
    >
      {body}
    </div>
  )
}
